import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import { useCart } from '@/contexts/CartContext';
import { CyberHeader } from '@/components/CyberHeader';
import { HEADER_TOTAL_H, TOP_BAR_H } from '@/components/CyberHeader';
import { CartDrawer } from '@/components/CartDrawer';
import HoloDossier from '@/components/HoloDossier';
import SaleBanner from '@/components/SaleBanner';
import EmployeeCard from '@/components/EmployeeCard';
import { CinematicWorkplaces } from '@/components/CinematicWorkplaces';
import FeaturedAgent from '@/components/FeaturedAgent';
import HoloVideoPlayer from '@/components/HoloVideoPlayer';
import { Search, Zap, Users, DollarSign, Clock, ChevronRight, Star, Shield, Cpu, TrendingUp, Globe, Code, BarChart3, ShoppingBag, Scale, Briefcase, Headphones, Palette, Target, ExternalLink, Download, Package, Gamepad2, Glasses, Map, Gavel, Heart, Home as HomeIcon, BookOpen, Rocket } from 'lucide-react';
import { ParticleField } from '@/components/ParticleField';
import { toast } from 'sonner';
import { useLocation } from 'wouter';
import { sfxClick, sfxHover } from '@/lib/soundFX';
import { HarnessModal, HARNESS_CONFIGS } from '@/components/HarnessModal';
import React from 'react';
import { Link } from 'wouter';
import { useIsMobile } from '@/hooks/useMobile';

// ── Neural Uplink Email Capture Form ─────────────────────────
function NeuralUplinkForm() {
  const [nlEmail, setNlEmail] = React.useState<string>('');
  const [nlName, setNlName] = React.useState<string>('');
  const [nlPhone, setNlPhone] = React.useState<string>('');
  const [nlInquiry, setNlInquiry] = React.useState<string>('');
  const [nlDone, setNlDone] = React.useState<boolean>(false);
  const subscribeMutation = (trpc as any).newsletter.subscribe.useMutation({
    onSuccess: (data: { success: boolean; alreadySubscribed: boolean }) => {
      if (data.alreadySubscribed) {
        toast.info(`⚡ Already linked — you’re in the system.`);
      } else {
        toast.success('⚡ NEURAL UPLINK ACTIVATED — Check your inbox!');
        setNlDone(true);
      }
    },
    onError: () => toast.error('Connection failed. Try again.'),
  });

  const inputStyle: React.CSSProperties = {
    background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(0,255,136,0.2)', color: '#E8F4FF',
    padding: '0.75rem 1rem', fontFamily: 'Share Tech Mono, monospace', fontSize: '0.78rem',
    letterSpacing: '0.1em', outline: 'none', width: '100%', boxSizing: 'border-box',
  };
  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => (e.target.style.borderColor = 'rgba(0,255,136,0.5)');
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => (e.target.style.borderColor = 'rgba(0,255,136,0.2)');
  const handleSubmit = () => {
    if (nlEmail) subscribeMutation.mutate({ email: nlEmail, name: nlName || undefined, phone: nlPhone || undefined, inquiry: nlInquiry || undefined });
  };

  if (nlDone) return (
    <div style={{ textAlign: 'center', marginTop: '3rem', padding: '2rem', border: '1px solid rgba(0,255,136,0.3)', background: 'rgba(0,255,136,0.04)' }}>
      <div style={{ fontFamily: 'Orbitron, monospace', fontSize: '1.1rem', color: '#00FF88', letterSpacing: '0.15em', marginBottom: '0.5rem' }}>✓ NEURAL UPLINK ACTIVATED</div>
      <div style={{ fontFamily: 'Rajdhani, sans-serif', color: 'rgba(232,244,255,0.5)', fontSize: '0.9rem' }}>Welcome to the command network. Check your inbox.</div>
    </div>
  );
  return (
    <div style={{ marginTop: '3rem', maxWidth: 600, margin: '3rem auto 0', padding: '2rem', border: '1px solid rgba(0,255,136,0.2)', background: 'rgba(0,255,136,0.03)', position: 'relative' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent, #00FF88, transparent)' }} />
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <div style={{ fontFamily: 'Orbitron, monospace', fontWeight: 700, fontSize: '0.92rem', color: '#00FF88', letterSpacing: '0.25em', marginBottom: '0.5rem' }}>⚡ JOIN THE COMMAND NETWORK</div>
        <div style={{ fontFamily: 'Rajdhani, sans-serif', color: 'rgba(232,244,255,0.45)', fontSize: '0.9rem' }}>Priority access to new agents, exclusive bundles &amp; intelligence briefings</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <input
            type="text"
            placeholder="FULL NAME (optional)"
            value={nlName}
            onChange={e => setNlName(e.target.value)}
            style={inputStyle}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          <input
            type="tel"
            placeholder="PHONE NUMBER (optional)"
            value={nlPhone}
            onChange={e => setNlPhone(e.target.value)}
            style={inputStyle}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>
        <input
          type="email"
          placeholder="SECURE COMMS CHANNEL (email) *"
          value={nlEmail}
          onChange={e => setNlEmail(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
          style={inputStyle}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        <select
          value={nlInquiry}
          onChange={e => setNlInquiry(e.target.value)}
          style={{
            ...inputStyle,
            appearance: 'none' as const,
            cursor: 'pointer',
            color: nlInquiry ? '#E8F4FF' : 'rgba(232,244,255,0.35)',
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
        >
          <option value="" style={{ background: '#070912', color: 'rgba(232,244,255,0.35)' }}>SELECT INQUIRY TYPE (optional)</option>
          <option value="general_info" style={{ background: '#070912', color: '#E8F4FF' }}>General Information</option>
          <option value="pricing" style={{ background: '#070912', color: '#E8F4FF' }}>Pricing &amp; Plans</option>
          <option value="custom_agent" style={{ background: '#070912', color: '#E8F4FF' }}>Custom AI Employee Request</option>
          <option value="enterprise" style={{ background: '#070912', color: '#E8F4FF' }}>Enterprise / Bulk Licensing</option>
          <option value="partnership" style={{ background: '#070912', color: '#E8F4FF' }}>Partnership Inquiry</option>
          <option value="technical_support" style={{ background: '#070912', color: '#E8F4FF' }}>Technical Support</option>
          <option value="demo" style={{ background: '#070912', color: '#E8F4FF' }}>Request a Demo</option>
        </select>
        <button
          onClick={handleSubmit}
          disabled={!nlEmail || subscribeMutation.isPending}
          style={{
            background: subscribeMutation.isPending ? 'rgba(0,255,136,0.2)' : 'linear-gradient(135deg, rgba(0,255,136,0.25), rgba(0,255,136,0.1))',
            border: '1px solid rgba(0,255,136,0.4)', color: '#00FF88',
            padding: '0.85rem 1.25rem', fontFamily: 'Orbitron, monospace', fontSize: '0.72rem',
            letterSpacing: '0.2em', cursor: nlEmail ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s ease', width: '100%',
          }}
        >
          {subscribeMutation.isPending ? 'LINKING...' : '⚡ UPLINK — JOIN THE COMMAND NETWORK'}
        </button>
      </div>
      <div style={{ marginTop: '0.75rem', textAlign: 'center', fontFamily: 'Share Tech Mono, monospace', fontSize: '0.65rem', color: 'rgba(232,244,255,0.25)', letterSpacing: '0.08em' }}>
        * Email required. All other fields optional. No spam — ever.
      </div>
    </div>
  );
}

// ── Types ──────────────────────────────────────────────────
interface Employee {
  id: number;
  name: string;
  roleTitle: string;
  departmentId: string;
  bio?: string | null;
  skills?: string[];
  price?: number | string | null;
  photoUrl?: string;
  availabilityStatus?: string;
}

interface Department {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  employeeCount?: number;
}

// ── Department Config ──────────────────────────────────────
const DEPT_CONFIG: Record<string, { icon: React.ReactNode; color: string; gradient: string; tagline: string }> = {
  'small-business': { icon: <Briefcase size={24} />, color: '#A855F7', gradient: 'linear-gradient(135deg, rgba(168,85,247,0.2), rgba(168,85,247,0.05))', tagline: 'Founder & SMB Operators' },
  'marketing': { icon: <Target size={24} />, color: '#FF0055', gradient: 'linear-gradient(135deg, rgba(255,0,85,0.2), rgba(255,0,85,0.05))', tagline: 'Revenue Generation Engines' },
  'sales': { icon: <TrendingUp size={24} />, color: '#00FF88', gradient: 'linear-gradient(135deg, rgba(0,255,136,0.2), rgba(0,255,136,0.05))', tagline: 'Sales & Pipeline AI' },
  'customer-service': { icon: <Headphones size={24} />, color: '#FF6B00', gradient: 'linear-gradient(135deg, rgba(255,107,0,0.2), rgba(255,107,0,0.05))', tagline: 'Client Experience Masters' },
  'operations': { icon: <Shield size={24} />, color: '#00A6EB', gradient: 'linear-gradient(135deg, rgba(0,166,235,0.2), rgba(0,166,235,0.05))', tagline: 'Operations & Admin AI' },
  'finance': { icon: <DollarSign size={24} />, color: '#FFB800', gradient: 'linear-gradient(135deg, rgba(255,184,0,0.2), rgba(255,184,0,0.05))', tagline: 'Financial Intelligence' },
  'technology': { icon: <Code size={24} />, color: '#00FFFF', gradient: 'linear-gradient(135deg, rgba(0,255,255,0.2), rgba(0,255,255,0.05))', tagline: 'Full-Stack AI Engineers' },
  'enterprise': { icon: <Globe size={24} />, color: '#7CFC00', gradient: 'linear-gradient(135deg, rgba(124,252,0,0.2), rgba(124,252,0,0.05))', tagline: 'Enterprise & Specialized AI' },
  'hospitality': { icon: <Star size={24} />, color: '#FF1493', gradient: 'linear-gradient(135deg, rgba(255,20,147,0.2), rgba(255,20,147,0.05))', tagline: 'STR & Hospitality Ops' },
  'ecommerce': { icon: <ShoppingBag size={24} />, color: '#FF8C00', gradient: 'linear-gradient(135deg, rgba(255,140,0,0.2), rgba(255,140,0,0.05))', tagline: 'E-Commerce Dominators' },
  'game-dev': { icon: <Gamepad2 size={24} />, color: '#A855F7', gradient: 'linear-gradient(135deg, rgba(168,85,247,0.2), rgba(168,85,247,0.05))', tagline: 'Unity · Unreal · Godot · Roblox' },
  'spatial-xr': { icon: <Glasses size={24} />, color: '#06B6D4', gradient: 'linear-gradient(135deg, rgba(6,182,212,0.2), rgba(6,182,212,0.05))', tagline: 'visionOS · WebXR · Spatial UI' },
  'geospatial': { icon: <Map size={24} />, color: '#10B981', gradient: 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(16,185,129,0.05))', tagline: 'GIS · Mapping · Spatial Data' },
  'legal': { icon: <Gavel size={24} />, color: '#F59E0B', gradient: 'linear-gradient(135deg, rgba(245,158,11,0.2), rgba(245,158,11,0.05))', tagline: 'Legal Ops & Document AI' },
  'healthcare': { icon: <Heart size={24} />, color: '#EF4444', gradient: 'linear-gradient(135deg, rgba(239,68,68,0.2), rgba(239,68,68,0.05))', tagline: 'Medical Billing & Patient Ops' },
  'lending-realestate': { icon: <HomeIcon size={24} />, color: '#10B981', gradient: 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(16,185,129,0.05))', tagline: 'Loans · Real Estate · Deals' },
  'worldbuilding': { icon: <BookOpen size={24} />, color: '#8B5CF6', gradient: 'linear-gradient(135deg, rgba(139,92,246,0.2), rgba(139,92,246,0.05))', tagline: 'Narrative · Lore · World Design' },
  'product-management': { icon: <Rocket size={24} />, color: '#00A6EB', gradient: 'linear-gradient(135deg, rgba(0,166,235,0.2), rgba(0,166,235,0.05))', tagline: 'Roadmaps · Sprints · Insights' },
};

// ── Main Component ─────────────────────────────────────────
export default function Home() {
  const { sessionId, isCartOpen, openCart, closeCart, setCartCount } = useCart();
  const cartOpen = isCartOpen;
  const setCartOpen = (v: boolean) => v ? openCart() : closeCart();
  const isMobile = useIsMobile();
  const [selectedDept, setSelectedDept] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [dossierOpen, setDossierOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(24);
  const [matchWizardOpen, setMatchWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardAnswers, setWizardAnswers] = useState<Record<string, string>>({});
  const [wizardResults, setWizardResults] = useState<any[]>([]);
  const [heroVisible, setHeroVisible] = useState(false);
  const [sortBy, setSortBy] = useState<'default' | 'price-asc' | 'price-desc' | 'name' | 'dept' | 'newest'>('default');
  const [deptSearchQuery, setDeptSearchQuery] = useState('');
  const [activeHarness, setActiveHarness] = useState<string | null>(null);

  const sectionsRef = useRef<Record<string, HTMLElement | null>>({});

  // ── Parse dept filter from URL query param (e.g. /?dept=sales-marketing) ──
  const [location, navigate] = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const deptParam = params.get('dept');
    if (deptParam) {
      setSelectedDept(deptParam);
      // Scroll to workforce section after a brief delay
      setTimeout(() => {
        const el = sectionsRef.current['workforce'];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 600);
    }
  }, [location]);

  // ── Data Fetching ──────────────────────────────────────────
  const { data: employees = [] } = trpc.employees.getAll.useQuery();
  const { data: departments = [] } = trpc.departments.getAll.useQuery();
  const { data: cartData, refetch: refetchCart } = trpc.cart.get.useQuery({ sessionId });
 const addToCartMutation = trpc.cart.add.useMutation({
   onSuccess: () => {
     refetchCart().then(res => {
        const count = (res.data as any)?.employees?.length ?? 0;
       setCartCount(count);
     });
      toast.success('Agent added to Data Core!', { style: { background: '#0B0D18', border: '1px solid rgba(0,166,235,0.4)', color: '#00A6EB' } });
      setTimeout(() => setCartOpen(true), 400);
    },
    onError: () => toast.error('Failed to add agent'),
  });

 const cartCount = cartData?.items?.length ?? 0;
  // Sync cart count badge on initial load
  useEffect(() => {
    if (cartData?.employees?.length !== undefined) {
      setCartCount(cartData.employees.length);
    }
  }, [cartData?.employees?.length, setCartCount]);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  // ── Filtered Employees ─────────────────────────────────────
  const filteredEmployees = useMemo(() => {
    let list = [...employees];
    if (selectedDept !== 'all') list = list.filter(e => e.departmentId === selectedDept);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(e =>
        e.name.toLowerCase().includes(q) ||
        e.roleTitle.toLowerCase().includes(q) ||
        (e.skills || []).some((s: string) => s.toLowerCase().includes(q))
      );
    }
    if (sortBy === 'price-asc') list.sort((a, b) => (Number(a.price) || 297) - (Number(b.price) || 297));
    else if (sortBy === 'price-desc') list.sort((a, b) => (Number(b.price) || 297) - (Number(a.price) || 297));
    else if (sortBy === 'name') list.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === 'dept') list.sort((a, b) => String(a.departmentId).localeCompare(String(b.departmentId)));
    else if (sortBy === 'newest') list.sort((a, b) => new Date((b as any).createdAt ?? 0).getTime() - new Date((a as any).createdAt ?? 0).getTime());
    return list;
  }, [employees, selectedDept, searchQuery, sortBy]);

  const visibleEmployees = filteredEmployees.slice(0, visibleCount);

  // ── Scroll to section ──────────────────────────────────────
  const scrollToSection = useCallback((section: string) => {
    const el = sectionsRef.current[section];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleAddToCart = useCallback((emp: Employee) => {
    addToCartMutation.mutate({ sessionId, employeeId: emp.id });
  }, [sessionId, addToCartMutation]);

  const handleViewDossier = useCallback((emp: Employee) => {
    setSelectedEmployee(emp);
    setDossierOpen(true);
  }, []);

  // ── Wizard Logic ───────────────────────────────────────────
  const wizardQuestions = [
    { id: 'bottleneck', label: 'What is your biggest operational bottleneck?', options: ['Sales & Revenue', 'Content & Marketing', 'Tech & Development', 'Finance & Reporting', 'Customer Support', 'Data & Analytics'] },
    { id: 'hours', label: 'How many hours/week is this costing you?', options: ['1–5 hrs', '5–15 hrs', '15–30 hrs', '30–50 hrs', '50+ hrs'] },
    { id: 'priority', label: 'What is your top priority right now?', options: ['Cut costs', 'Scale faster', 'Improve quality', 'Automate workflows', 'Enter new markets'] },
  ];

  const runWizard = useCallback(() => {
    const bottleneck = wizardAnswers.bottleneck || '';
    const deptMap: Record<string, string> = {
      'Sales & Revenue': 'sales',
      'Content & Marketing': 'marketing',
      'Tech & Development': 'technology',
      'Finance & Reporting': 'finance',
      'Customer Support': 'customer-service',
      'Data & Analytics': 'enterprise',
    };
    const targetDept = deptMap[bottleneck];
    const pool = targetDept ? employees.filter(e => e.departmentId === targetDept) : employees;
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    setWizardResults(shuffled.slice(0, 3));
    setWizardStep(3);
  }, [wizardAnswers, employees]);

  // ── ROI Stats ──────────────────────────────────────────────
  const roiStats = [
    { label: 'Avg Human Salary Saved', value: '$65,000', sub: 'per year per role', color: '#00FF88' },
    { label: 'Avg AI Employee Cost', value: '$297', sub: 'one-time deployment', color: '#00A6EB' },
    { label: 'First Year ROI', value: '$64,703', sub: 'net savings per agent', color: '#FFB800' },
    { label: 'Total Workforce ROI', value: '$14.2M+', sub: 'if you deploy all 232', color: '#FF0055' },
  ];

  const deptList = departments.length > 0 ? departments : Object.keys(DEPT_CONFIG).map(id => ({ id, name: id.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }));

  return (
    <div style={{ background: 'var(--cyber-black)', minHeight: '100vh', position: 'relative' }}>
      <CyberHeader onOpenCart={() => setCartOpen(true)} cartCount={cartCount} onScrollToSection={scrollToSection} onSetDept={setSelectedDept} />
      <CartDrawer isOpen={cartOpen} onClose={() => setCartOpen(false)} />
      {/* Spacer — pushes content below the fixed two-zone header (top bar + glass panel) */}
      {/* On desktop: full HEADER_TOTAL_H (top bar + glass panel both fixed).
          On mobile: only TOP_BAR_H — the neural grid is in-flow and scrolls with the page. */}
      <div className="header-spacer-mobile" style={{ height: HEADER_TOTAL_H }} />
      <SaleBanner />
      {dossierOpen && selectedEmployee && (
        <HoloDossier
          employee={selectedEmployee as any}
          onClose={() => setDossierOpen(false)}
          onAddToCart={() => { handleAddToCart(selectedEmployee); setDossierOpen(false); setTimeout(() => setCartOpen(true), 300); }}
        />
      )}

      {/* ── HERO SECTION ──────────────────────────────────── */}
      <section
        className="hero-section-mobile"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: '2rem',
          paddingBottom: '4rem',
          position: 'relative',
          overflow: 'visible',
        }}
      >
        {/* Radial glow background */}
        <ParticleField />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(0,166,235,0.06) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse 60% 40% at 80% 80%, rgba(255,0,85,0.04) 0%, transparent 60%)',
          pointerEvents: 'none',
        }} />

        {/* Hero content */}
        <div style={{
          textAlign: 'center', maxWidth: 900, width: '100%', boxSizing: 'border-box' as const, padding: '0 clamp(1rem, 5vw, 1.5rem)', position: 'relative', zIndex: 2,
          opacity: heroVisible ? 1 : 0,
          transform: heroVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
        }}>
          {/* Status badge */}
          <div className="system-online-bar" style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            background: 'rgba(0,166,235,0.08)', border: '1px solid rgba(0,166,235,0.25)',
            borderRadius: 2, padding: '0.35rem 1rem', marginBottom: '2rem',
            fontFamily: 'Share Tech Mono, monospace', fontSize: '0.74rem', color: 'rgba(0,166,235,0.8)',
            letterSpacing: '0.2em',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#00FF88', boxShadow: '0 0 8px #00FF88', animation: 'neonPulseCyan 2s ease-in-out infinite' }} />
            SYSTEM ONLINE — 232 AI AGENTS READY FOR DEPLOYMENT
          </div>

          {/* Main headline */}
          <h1 className="hero-title-block">
            <span className="hero-line-flow hero-line-1">DEPLOY YOUR</span>
            <span className="hero-line-flow hero-line-2">AI WORKFORCE</span>
            <span className="hero-line-flow hero-line-3">TODAY</span>
          </h1>

          {/* Subheadline */}
          <p className="hero-subheadline" style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: 'clamp(1.2rem, 2vw, 1.25rem)',
            fontWeight: 400,
            color: 'rgba(232,244,255,0.55)',
            maxWidth: '100%', margin: '0 auto 2.5rem', width: '100%', boxSizing: 'border-box' as const, padding: '0',
            lineHeight: 1.6,
            letterSpacing: '0.02em',
          }}>
            232 elite AI agents across 18 departments. No salaries. No HR. No onboarding delays.
            Deploy in minutes. Scale infinitely.
          </p>

          {/* CTA Buttons */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '3rem' }}>
            <button
              className="btn-cyber hero-cta-btn"
              style={{ fontSize: isMobile ? '0.78rem' : '0.84rem', padding: isMobile ? '0.8rem 1.5rem' : '0.8rem 2rem', whiteSpace: 'nowrap' }}
              onClick={() => scrollToSection('workforce')}
            >
              <Zap size={14} />
              BROWSE WORKFORCE
            </button>
            <button
              className="btn-cyber btn-magenta hero-cta-btn"
              style={{ fontSize: isMobile ? '0.78rem' : '0.84rem', padding: isMobile ? '0.8rem 1.5rem' : '0.8rem 2rem', whiteSpace: 'nowrap' }}
              onClick={() => { sfxClick(); navigate('/matchmaker'); }}
            >
              <Target size={14} />
              TALENT MATCHMAKER
            </button>
          </div>

          {/* Stats row */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem',
            maxWidth: 600, margin: '0 auto',
          }}>
            {[
              { num: '232', label: 'AI AGENTS' },
              { num: '18', label: 'DEPARTMENTS' },
              { num: '$0', label: 'MONTHLY SALARY' },
              { num: '24/7', label: 'AVAILABILITY' },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{
                  fontFamily: 'Orbitron, monospace', fontWeight: 900,
                  fontSize: 'clamp(1.4rem, 3vw, 2rem)',
                  background: ['linear-gradient(135deg, #00A6EB, #00FFFF)', 'linear-gradient(135deg, #FF0055, #FF6B00)', 'linear-gradient(135deg, #00FF88, #00A6EB)', 'linear-gradient(135deg, #FFB800, #FF6B00)'][i],
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                }}>{s.num}</div>
                <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.65rem', color: 'rgba(232,244,255,0.35)', letterSpacing: '0.2em', marginTop: '0.2rem' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

       {/* 3D Neural Core */}
        {/* Holographic Department Quick-Select Grid */}
        <div style={{ width: '100%', maxWidth: 1100, marginTop: '3rem', position: 'relative', zIndex: 2 }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.68rem', color: 'rgba(0,166,235,0.5)', letterSpacing: '0.3em' }}>◈ SELECT OPERATIONAL DIVISION ◈</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.75rem' }}>
            {deptList.map((d: any) => {
              const cfg = DEPT_CONFIG[d.id];
              const count = employees.filter((e: any) => e.departmentId === d.id).length;
              const isActive = selectedDept === d.id;
              return (
                <button
                  key={d.id}
                  onClick={() => { setSelectedDept(d.id); scrollToSection('departments'); }}
                  style={{
                    background: isActive
                      ? `linear-gradient(135deg, ${cfg?.color || '#00A6EB'}22, ${cfg?.color || '#00A6EB'}08)`
                      : 'rgba(6,8,16,0.8)',
                    border: `1px solid ${isActive ? (cfg?.color || '#00A6EB') : 'rgba(232,244,255,0.08)'}`,
                    borderRadius: 4,
                    padding: '0.75rem 0.5rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s cubic-bezier(0.23,1,0.32,1)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem',
                    position: 'relative', overflow: 'hidden',
                    boxShadow: isActive ? `0 0 20px ${cfg?.color || '#00A6EB'}33` : 'none',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = cfg?.color || '#00A6EB'; (e.currentTarget as HTMLElement).style.boxShadow = `0 0 16px ${cfg?.color || '#00A6EB'}22`; }}
                  onMouseLeave={e => { if (!isActive) { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(232,244,255,0.08)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; } }}
                >
                  <div style={{ fontSize: '1.2rem', filter: `drop-shadow(0 0 6px ${cfg?.color || '#00A6EB'})` }}>{cfg?.icon}</div>
                  <div style={{ fontFamily: 'Orbitron, monospace', fontSize: '0.45rem', fontWeight: 700, color: cfg?.color || '#00A6EB', letterSpacing: '0.12em', textAlign: 'center', lineHeight: 1.3 }}>
                    {d.name.replace(/ & /g, ' &\n').replace(/ /g, '\n')}
                  </div>
                  <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.65rem', color: 'rgba(232,244,255,0.35)', letterSpacing: '0.1em' }}>{count} AGENTS</div>
                  {isActive && <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: cfg?.color || '#00A6EB' }} />}
                </button>
              );
            })}
          </div>
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button
              onClick={() => { setSelectedDept('all'); scrollToSection('departments'); }}
              style={{
                background: selectedDept === 'all' ? 'rgba(0,166,235,0.12)' : 'transparent',
                border: `1px solid ${selectedDept === 'all' ? 'rgba(0,166,235,0.6)' : 'rgba(232,244,255,0.12)'}`,
                color: selectedDept === 'all' ? '#00A6EB' : 'rgba(232,244,255,0.4)',
                fontFamily: 'Orbitron, monospace', fontSize: '0.68rem', letterSpacing: '0.2em',
                padding: '0.5rem 2rem', cursor: 'pointer', borderRadius: 2,
                transition: 'all 0.2s ease',
              }}
            >⬡ VIEW ALL 232 AGENTS ⬡</button>
          </div>
        </div>

        {/* Scroll hint */}
        <div style={{
          position: 'absolute', bottom: '2rem', left: '50%', transform: 'translateX(-50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem',
          animation: 'floatUp 2s ease-in-out infinite',
          opacity: 0.4,
        }}>
          <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.65rem', color: '#00A6EB', letterSpacing: '0.3em' }}>SCROLL TO EXPLORE</div>
          <div style={{ width: 1, height: 40, background: 'linear-gradient(180deg, rgba(0,166,235,0.6), transparent)' }} />
        </div>
      </section>

      {/* ── DEPARTMENT COMMAND ─────────────────────────────── */}
      {/* ── INTEL BROADCAST — HoloVideoPlayer (directly below hero) ── */}
      <section style={{ padding: '0 0 4rem', position: 'relative', zIndex: 1 }}>
        <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(0,166,235,0.4), rgba(168,85,247,0.3), rgba(255,0,85,0.25), rgba(0,166,235,0.4), transparent)', marginBottom: '3rem' }} />
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.72rem', color: 'rgba(0,166,235,0.55)', letterSpacing: '0.3em', marginBottom: '0.6rem' }}>◈ INTEL BROADCAST — LIVE AGENT FEED ◈</div>
            <h2 style={{ fontFamily: 'Orbitron, monospace', fontWeight: 900, fontSize: 'clamp(1.8rem, 3vw, 2.4rem)', color: 'rgba(232,244,255,0.95)', letterSpacing: '-0.01em', lineHeight: 1.1, textShadow: '0 0 40px rgba(0,166,235,0.15)' }}>
              MEET YOUR <span style={{ color: '#00A6EB', textShadow: '0 0 30px rgba(0,166,235,0.7), 0 0 60px rgba(0,166,235,0.3)' }}>AI WORKFORCE</span>
            </h2>
          </div>
          {/* Mobile: break out of container padding so player fills full width */}
          <style>{`
            @media (max-width: 900px) {
              .holo-player-wrap {
                margin-left: -1.5rem !important;
                margin-right: -1.5rem !important;
                border-radius: 0 !important;
                border-left: none !important;
                border-right: none !important;
                width: calc(100% + 3rem) !important;
                max-width: calc(100% + 3rem) !important;
                overflow: hidden !important;
              }
            }
            @media (max-width: 480px) {
              .holo-player-wrap {
                margin-left: -0.75rem !important;
                margin-right: -0.75rem !important;
                width: calc(100% + 1.5rem) !important;
                max-width: calc(100% + 1.5rem) !important;
              }
            }
          `}</style>
          <div className="holo-player-wrap" style={{ borderRadius: 6, overflow: 'hidden', boxShadow: '0 0 60px rgba(0,166,235,0.08), 0 0 120px rgba(0,0,0,0.6)', border: '1px solid rgba(0,166,235,0.12)' }}>
            <HoloVideoPlayer />
          </div>
        </div>
      </section>
      <section
        ref={el => { sectionsRef.current['departments'] = el; }}
        style={{ padding: '6rem 0', position: 'relative', zIndex: 1 }}
      >
        {/* Section separator */}
        <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(0,166,235,0.3), rgba(255,0,85,0.2), rgba(0,166,235,0.3), transparent)', marginBottom: '4rem' }} />

        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div className="section-label" style={{ justifyContent: 'center' }}>◈ SELECT DIVISION ◈</div>
            <h2 style={{
              fontFamily: 'Orbitron, monospace', fontWeight: 900,
              fontSize: 'clamp(2.2rem, 4vw, 3rem)',
              color: 'rgba(232,244,255,0.95)', letterSpacing: '-0.01em',
            }}>
              DEPARTMENT <span style={{ color: '#00A6EB', textShadow: '0 0 20px rgba(0,166,235,0.5)' }}>COMMAND</span>
            </h2>
            <p style={{ fontFamily: 'Rajdhani, sans-serif', color: 'rgba(232,244,255,0.45)', marginTop: '0.75rem', fontSize: '1rem', letterSpacing: '0.05em' }}>
            Select a division to filter the AI workforce roster
          </p>
        </div>

          {/* Department search bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            background: 'rgba(11,13,24,0.8)', border: '1px solid rgba(0,166,235,0.2)',
            borderRadius: 3, padding: '0.5rem 1rem', maxWidth: 420, margin: '0 auto 1.5rem',
            transition: 'border-color 0.2s ease',
          }}
            onFocus={e => (e.currentTarget.style.borderColor = 'rgba(0,166,235,0.5)')}
            onBlur={e => (e.currentTarget.style.borderColor = 'rgba(0,166,235,0.2)')}
          >
            <Search size={13} style={{ color: 'rgba(0,166,235,0.5)', flexShrink: 0 }} />
            <input
              type="text"
              placeholder="SEARCH DEPARTMENTS..."
              value={deptSearchQuery}
              onChange={e => setDeptSearchQuery(e.target.value)}
              style={{
                flex: 1, background: 'none', border: 'none', outline: 'none',
                fontFamily: 'Share Tech Mono, monospace', fontSize: '0.76rem',
                color: 'rgba(232,244,255,0.8)', letterSpacing: '0.1em',
              }}
            />
            {deptSearchQuery && (
              <button onClick={() => setDeptSearchQuery('')} style={{ background: 'none', border: 'none', color: 'rgba(232,244,255,0.3)', cursor: 'pointer', padding: 0, fontSize: '0.9rem', lineHeight: 1 }}>✕</button>
            )}
          </div>

          {/* All Departments button */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
            <button
              onClick={() => setSelectedDept('all')}
              style={{
                fontFamily: 'Orbitron, monospace', fontSize: '0.74rem', fontWeight: 700,
                letterSpacing: '0.2em', padding: '0.6rem 2rem',
                background: selectedDept === 'all' ? 'rgba(0,166,235,0.2)' : 'transparent',
                border: `1px solid ${selectedDept === 'all' ? 'rgba(0,166,235,0.6)' : 'rgba(0,166,235,0.2)'}`,
                color: selectedDept === 'all' ? '#00A6EB' : 'rgba(232,244,255,0.4)',
                cursor: 'pointer', borderRadius: 2, transition: 'all 0.2s ease',
                boxShadow: selectedDept === 'all' ? '0 0 20px rgba(0,166,235,0.15)' : 'none',
              }}
            >
              ◈ ALL DIVISIONS — FULL WORKFORCE (232 AGENTS)
            </button>
          </div>

          {/* Department grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '1rem',
          }}>
            {deptList.filter((dept: Department) => !deptSearchQuery.trim() || dept.name.toLowerCase().includes(deptSearchQuery.toLowerCase())).map((dept: Department) => {
              const config = DEPT_CONFIG[dept.id] || { icon: <Briefcase size={24} />, color: '#00A6EB', gradient: 'linear-gradient(135deg, rgba(0,166,235,0.15), rgba(0,166,235,0.05))', tagline: 'AI Specialists' };
              const isActive = selectedDept === dept.id;
              const empCount = employees.filter(e => e.departmentId === dept.id).length;

              return (
                <div
                  key={dept.id}
                  className="dept-card"
                  onClick={() => setSelectedDept(isActive ? 'all' : dept.id)}
                  style={{
                    padding: '1.25rem',
                    borderColor: isActive ? config.color : 'rgba(0,166,235,0.12)',
                    background: isActive ? config.gradient : 'rgba(11,13,24,0.7)',
                    boxShadow: isActive ? `0 0 30px rgba(${hexToRgb(config.color)}, 0.15), 0 8px 30px rgba(0,0,0,0.4)` : 'none',
                  }}
                >
                  {/* Icon */}
                  <div style={{
                    width: 44, height: 44, borderRadius: 4,
                    background: `rgba(${hexToRgb(config.color)}, 0.12)`,
                    border: `1px solid rgba(${hexToRgb(config.color)}, 0.3)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: config.color, marginBottom: '0.75rem',
                    boxShadow: isActive ? `0 0 15px rgba(${hexToRgb(config.color)}, 0.3)` : 'none',
                    transition: 'box-shadow 0.3s ease',
                  }}>
                    {config.icon}
                  </div>

                  {/* Name */}
                  <div style={{
                    fontFamily: 'Orbitron, monospace', fontWeight: 700, fontSize: '0.74rem',
                    color: isActive ? config.color : 'rgba(232,244,255,0.8)',
                    letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.25rem',
                    lineHeight: 1.3,
                  }}>
                    {dept.name}
                  </div>

                  {/* Tagline */}
                  <div style={{
                    fontFamily: 'Rajdhani, sans-serif', fontSize: '0.88rem',
                    color: 'rgba(232,244,255,0.35)', marginBottom: '0.75rem', lineHeight: 1.3,
                  }}>
                    {config.tagline}
                  </div>

                  {/* Count */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{
                      fontFamily: 'Share Tech Mono, monospace', fontSize: '0.68rem',
                      color: 'rgba(232,244,255,0.3)', letterSpacing: '0.1em',
                    }}>
                      {empCount} AGENTS
                    </span>
                    <ChevronRight size={12} style={{ color: config.color, opacity: isActive ? 1 : 0.3 }} />
                  </div>

                  {/* Active indicator bar */}
                  {isActive && (
                    <div style={{
                      position: 'absolute', bottom: 0, left: 0, right: 0, height: 2,
                      background: `linear-gradient(90deg, transparent, ${config.color}, transparent)`,
                    }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FEATURED AGENT SPOTLIGHT ─────────────────────── */}
      {employees && employees.length > 0 && (
      <>
        <FeaturedAgent
          employees={(employees as any[]).slice(0, 5)}
          onView={handleViewDossier as any}
          onAddToCart={handleAddToCart as any}
        />
      </>
      )}

      {/* ── FULL WORKFORCE ROSTER ──────────────────────────── */}
      <section
        ref={el => { sectionsRef.current['workforce'] = el; }}
        style={{ padding: '4rem 0 6rem', position: 'relative', zIndex: 1 }}
      >
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <div className="section-label" style={{ justifyContent: 'center' }}>◈ ACTIVE ROSTER ◈</div>
            <h2 style={{
              fontFamily: 'Orbitron, monospace', fontWeight: 900,
              fontSize: 'clamp(2.2rem, 4vw, 3rem)',
              color: 'rgba(232,244,255,0.95)',
            }}>
              FULL <span style={{ color: '#00A6EB', textShadow: '0 0 20px rgba(0,166,235,0.5)' }}>WORKFORCE</span>
            </h2>
            <p style={{ fontFamily: 'Rajdhani, sans-serif', color: 'rgba(232,244,255,0.4)', marginTop: '0.5rem', fontSize: '0.9rem' }}>
              {filteredEmployees.length} agents available for deployment
            </p>
          </div>

          {/* Search bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            background: 'rgba(11,13,24,0.8)', border: '1px solid rgba(0,166,235,0.2)',
            borderRadius: 3, padding: '0.6rem 1rem', maxWidth: 500, margin: '0 auto 2.5rem',
            transition: 'border-color 0.2s ease',
          }}
            onFocus={e => (e.currentTarget.style.borderColor = 'rgba(0,166,235,0.5)')}
            onBlur={e => (e.currentTarget.style.borderColor = 'rgba(0,166,235,0.2)')}
          >
            <Search size={14} style={{ color: 'rgba(0,166,235,0.5)', flexShrink: 0 }} />
            <input
              type="text"
              placeholder="SEARCH AGENTS, SKILLS, DEPARTMENTS..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                flex: 1, background: 'none', border: 'none', outline: 'none',
                fontFamily: 'Share Tech Mono, monospace', fontSize: '0.78rem',
                color: 'rgba(232,244,255,0.8)', letterSpacing: '0.1em',
              }}
            />
          </div>

          {/* Sort dropdown */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.5rem' }}>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as any)}
              aria-label="Sort agents"
              style={{
                background: 'rgba(11,13,24,0.8)', border: '1px solid rgba(0,166,235,0.2)',
                color: 'rgba(232,244,255,0.7)', fontFamily: 'Share Tech Mono, monospace',
                fontSize: '0.74rem', letterSpacing: '0.1em', padding: '0.4rem 0.75rem',
                cursor: 'pointer', outline: 'none', borderRadius: 2,
              }}
            >
              <option value="default">◈ SORT: DEFAULT</option>
              <option value="price-asc">◈ PRICE: LOW → HIGH</option>
              <option value="price-desc">◈ PRICE: HIGH → LOW</option>
              <option value="name">◈ NAME: A → Z</option>
              <option value="dept">◈ DEPARTMENT: A → Z</option>
              <option value="newest">◈ NEWEST FIRST</option>
            </select>
          </div>

          {/* Employee Grid */}
          {visibleEmployees.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: 'rgba(232,244,255,0.3)' }}>
              <div style={{ fontFamily: 'Orbitron, monospace', fontSize: '0.92rem', letterSpacing: '0.2em' }}>NO AGENTS MATCH QUERY</div>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: '1rem',
            }}>
              {visibleEmployees.map((emp: any, i: number) => {
                const config = DEPT_CONFIG[emp.departmentId] || { color: '#00A6EB', gradient: '', tagline: '' };
                return (
                  <EmployeeCard
                    key={emp.id}
                    employee={emp}
                    deptColor={config.color}
                    onView={handleViewDossier}
                    onAddToCart={handleAddToCart}
                    index={i}
                  />
                );
              })}
            </div>
          )}

          {/* Load more */}
          {visibleCount < filteredEmployees.length && (
            <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
              <button
                className="btn-cyber"
                onClick={() => setVisibleCount(v => v + 24)}
                style={{ fontSize: '0.78rem', padding: '0.7rem 2rem' }}
              >
                LOAD MORE AGENTS ({filteredEmployees.length - visibleCount} REMAINING)
              </button>
            </div>
          )}
        </div>
      </section>


      {/* ── ROI COMMAND CENTER ─────────────────────────────── */}
      <section
        ref={el => { sectionsRef.current['pricing'] = el; }}
        style={{ padding: '5rem 0', position: 'relative', zIndex: 1 }}
      >
        <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(0,166,235,0.3), transparent)', marginBottom: '4rem' }} />
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div className="section-label" style={{ justifyContent: 'center' }}>◈ THE MATH IS UNDENIABLE ◈</div>
            <h2 style={{ fontFamily: 'Orbitron, monospace', fontWeight: 900, fontSize: 'clamp(2.2rem, 4vw, 3rem)', color: 'rgba(232,244,255,0.95)' }}>
              ROI <span style={{ color: '#00FF88', textShadow: '0 0 20px rgba(0,255,136,0.5)' }}>COMMAND</span> CENTER
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem', maxWidth: 900, margin: '0 auto' }}>
            {roiStats.map((stat, i) => (
              <div key={i} style={{
                background: 'rgba(11,13,24,0.8)',
                border: `1px solid rgba(${hexToRgb(stat.color)}, 0.2)`,
                padding: '1.75rem 1.5rem',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                clipPath: 'polygon(10px 0%, 100% 0%, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0% 100%, 0% 10px)',
                transition: 'all 0.3s ease',
              }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.borderColor = `rgba(${hexToRgb(stat.color)}, 0.5)`;
                  (e.currentTarget as HTMLElement).style.boxShadow = `0 0 30px rgba(${hexToRgb(stat.color)}, 0.1)`;
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.borderColor = `rgba(${hexToRgb(stat.color)}, 0.2)`;
                  (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                }}
              >
                <div style={{
                  fontFamily: 'Orbitron, monospace', fontWeight: 900,
                  fontSize: 'clamp(1.8rem, 3vw, 2.2rem)',
                  color: stat.color,
                  textShadow: `0 0 20px rgba(${hexToRgb(stat.color)}, 0.5)`,
                  marginBottom: '0.5rem',
                }}>
                  {stat.value}
                </div>
                <div style={{ fontFamily: 'Orbitron, monospace', fontSize: '0.68rem', color: 'rgba(232,244,255,0.6)', letterSpacing: '0.15em', marginBottom: '0.25rem' }}>
                  {stat.label}
                </div>
                <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.65rem', color: 'rgba(232,244,255,0.25)', letterSpacing: '0.1em' }}>
                  {stat.sub}
                </div>
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${stat.color}, transparent)`, opacity: 0.5 }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CINEMATIC WORKPLACE ENVIRONMENTS ───────────────── */}
      <CinematicWorkplaces onScrollToSection={(s) => {
        const el = sectionsRef.current[s];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }} />

      {/* ── HOW TO DEPLOY ──────────────────────────────────── */}
      <section
        ref={el => { sectionsRef.current['about'] = el; }}
        style={{ padding: '5rem 0', position: 'relative', zIndex: 1 }}
      >
        <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,0,85,0.3), transparent)', marginBottom: '4rem' }} />
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div className="section-label" style={{ justifyContent: 'center' }}>◈ DEPLOYMENT PROTOCOL ◈</div>
            <h2 style={{ fontFamily: 'Orbitron, monospace', fontWeight: 900, fontSize: 'clamp(2.2rem, 4vw, 3rem)', color: 'rgba(232,244,255,0.95)' }}>
              HOW TO <span style={{ color: '#FF0055', textShadow: '0 0 20px rgba(255,0,85,0.5)' }}>DEPLOY</span>
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem', maxWidth: 900, margin: '0 auto' }}>
            {[
              { step: '01', icon: <Users size={28} />, title: 'BROWSE ROSTER', desc: 'Explore 232 elite AI agents across 18 specialized departments. Filter by role, skill, or department.', color: '#00A6EB' },
              { step: '02', icon: <Star size={28} />, title: 'SELECT AGENTS', desc: 'Open the holographic dossier for any agent. Review capabilities, bio, and value proposition.', color: '#FF0055' },
              { step: '03', icon: <Shield size={28} />, title: 'SECURE CHECKOUT', desc: 'Add agents to your Data Core. Complete secure Stripe checkout. Bundle discounts auto-apply.', color: '#FFB800' },
              { step: '04', icon: <Zap size={28} />, title: 'INSTANT DEPLOY', desc: 'Receive your encrypted agent package immediately. Deploy to any AI harness in minutes.', color: '#00FF88' },
            ].map((s, i) => (
              <div key={i} style={{
                background: 'rgba(11,13,24,0.7)',
                border: `1px solid rgba(${hexToRgb(s.color)}, 0.15)`,
                padding: '2rem 1.5rem',
                position: 'relative',
                clipPath: 'polygon(10px 0%, 100% 0%, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0% 100%, 0% 10px)',
                transition: 'all 0.3s ease',
              }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.borderColor = `rgba(${hexToRgb(s.color)}, 0.4)`;
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.borderColor = `rgba(${hexToRgb(s.color)}, 0.15)`;
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                }}
              >
                <div style={{
                  fontFamily: 'Orbitron, monospace', fontWeight: 900, fontSize: '2.5rem',
                  color: `rgba(${hexToRgb(s.color)}, 0.12)`,
                  position: 'absolute', top: '0.75rem', right: '1rem', lineHeight: 1,
                }}>
                  {s.step}
                </div>
                <div style={{ color: s.color, marginBottom: '1rem' }}>{s.icon}</div>
                <div style={{ fontFamily: 'Orbitron, monospace', fontWeight: 700, fontSize: '0.78rem', color: s.color, letterSpacing: '0.15em', marginBottom: '0.75rem' }}>
                  {s.title}
                </div>
                <div style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: '0.9rem', color: 'rgba(232,244,255,0.5)', lineHeight: 1.6 }}>
                  {s.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NEURAL UPLINK ──────────────────────────────────── */}
      <section
        ref={el => { sectionsRef.current['integrations'] = el; }}
        style={{ padding: '5rem 0', position: 'relative', zIndex: 1 }}
      >
        <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(0,255,136,0.3), transparent)', marginBottom: '4rem' }} />
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div className="section-label" style={{ justifyContent: 'center' }}>◈ COMPATIBLE HARNESSES ◈</div>
            <h2 style={{ fontFamily: 'Orbitron, monospace', fontWeight: 900, fontSize: 'clamp(2.2rem, 4vw, 3rem)', color: 'rgba(232,244,255,0.95)' }}>
              NEURAL <span style={{ color: '#00FF88', textShadow: '0 0 20px rgba(0,255,136,0.5)' }}>UPLINK</span>
            </h2>
            <p style={{ fontFamily: 'Rajdhani, sans-serif', color: 'rgba(232,244,255,0.4)', marginTop: '0.75rem', fontSize: '1rem' }}>
              All agents deploy to any major AI platform
            </p>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center', maxWidth: 800, margin: '0 auto' }}>
            {['ChatGPT', 'Claude', 'Gemini', 'Grok', 'Make.com', 'Zapier', 'n8n', 'Voiceflow', 'Botpress', 'AutoGPT', 'AgentGPT', 'LangChain', 'CrewAI', 'OpenAI API', 'Anthropic API', 'Perplexity'].map((h, i) => (
              <div key={i} style={{
                fontFamily: 'Share Tech Mono, monospace', fontSize: '0.74rem', letterSpacing: '0.1em',
                color: 'rgba(0,255,136,0.7)', background: 'rgba(0,255,136,0.05)',
                border: '1px solid rgba(0,255,136,0.15)', borderRadius: 2,
                padding: '0.4rem 0.9rem', cursor: 'default',
                transition: 'all 0.2s ease',
              }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.background = 'rgba(0,255,136,0.12)';
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,255,136,0.4)';
                  (e.currentTarget as HTMLElement).style.color = '#00FF88';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.background = 'rgba(0,255,136,0.05)';
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,255,136,0.15)';
                  (e.currentTarget as HTMLElement).style.color = 'rgba(0,255,136,0.7)';
                }}
              >
                {h}
              </div>
            ))}
          </div>
          <NeuralUplinkForm />
        </div>
      </section>

      {/* ── POWER HARNESSES ──────────────────────────────────────── */}
      <section style={{ padding: '5rem 0', position: 'relative', zIndex: 1 }}>
        <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(168,85,247,0.3), rgba(0,166,235,0.2), rgba(168,85,247,0.3), transparent)', marginBottom: '4rem' }} />
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div className="section-label" style={{ justifyContent: 'center' }}>◈ POWER HARNESSES ◈</div>
            <h2 style={{ fontFamily: 'Orbitron, monospace', fontWeight: 900, fontSize: 'clamp(2.2rem, 4vw, 3rem)', color: 'rgba(232,244,255,0.95)' }}>
              BUILD ENTIRE <span style={{ color: '#A855F7', textShadow: '0 0 20px rgba(168,85,247,0.5)' }}>DEPARTMENTS</span>
            </h2>
            <p style={{ fontFamily: 'Rajdhani, sans-serif', color: 'rgba(232,244,255,0.45)', marginTop: '0.75rem', fontSize: '1.05rem', maxWidth: 600, margin: '0.75rem auto 0' }}>
              Pair your Real AI Employees with these elite desktop harnesses to build fully autonomous teams, departments, and entire AI-powered organizations — without writing a single line of code.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {/* Hermes Agent Desktop */}
            <div style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.08) 0%, rgba(11,13,24,0.95) 100%)', border: '1px solid rgba(168,85,247,0.3)', borderRadius: 4, padding: '1.75rem', position: 'relative', overflow: 'hidden', clipPath: 'polygon(16px 0%,100% 0%,100% calc(100% - 16px),calc(100% - 16px) 100%,0% 100%,0% 16px)', transition: 'all 0.25s cubic-bezier(0.23,1,0.32,1)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(168,85,247,0.7)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 0 40px rgba(168,85,247,0.2)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(168,85,247,0.3)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent, #A855F7, transparent)' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ width: 44, height: 44, borderRadius: 4, background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>🧠</div>
                <div>
                  <div style={{ fontFamily: 'Orbitron, monospace', fontWeight: 900, fontSize: '0.96rem', color: '#A855F7', letterSpacing: '0.1em', textShadow: '0 0 12px rgba(168,85,247,0.5)' }}>HERMES AGENT DESKTOP</div>
                  <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.65rem', color: 'rgba(168,85,247,0.5)', letterSpacing: '0.15em' }}>BY ELITE NEXUS AI</div>
                </div>
              </div>
              <p style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: '0.9rem', color: 'rgba(232,244,255,0.55)', lineHeight: 1.65, marginBottom: '1.25rem' }}>
                A local AI desktop environment that lets you load and orchestrate multiple Real AI Employees simultaneously. Assign agents to dedicated workspaces, chain them into automated workflows, and run entire departments from a single holographic command center — all on your own hardware.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1.25rem' }}>
                {['Multi-Agent Orchestration', 'Local LLM Support', 'Voice Control', 'Department Builder'].map(tag => (
                  <span key={tag} style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.48rem', letterSpacing: '0.08em', color: 'rgba(168,85,247,0.7)', background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.2)', borderRadius: 2, padding: '0.25rem 0.6rem' }}>{tag}</span>
                ))}
              </div>
              <button onClick={() => { sfxClick(); window.open('https://hermes-agent.nousresearch.com/docs/user-guide/desktop', '_blank'); }}
                style={{ width: '100%', background: 'linear-gradient(135deg, rgba(168,85,247,0.25), rgba(168,85,247,0.1))', border: '1px solid rgba(168,85,247,0.5)', borderRadius: 3, padding: '0.7rem 1rem', cursor: 'pointer', fontFamily: 'Orbitron, monospace', fontWeight: 700, fontSize: '0.58rem', letterSpacing: '0.15em', color: '#A855F7', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', transition: 'all 0.2s ease', clipPath: 'polygon(6px 0%,100% 0%,100% calc(100% - 6px),calc(100% - 6px) 100%,0% 100%,0% 6px)' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(168,85,247,0.35)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 0 20px rgba(168,85,247,0.3)'; sfxHover(); }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'linear-gradient(135deg, rgba(168,85,247,0.25), rgba(168,85,247,0.1))'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}>
                <Download size={13} /> DOWNLOAD HERMES DESKTOP
              </button>
              <button onClick={() => { sfxClick(); setActiveHarness('hermes'); }}
                style={{ width: '100%', marginTop: '0.5rem', background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.2)', borderRadius: 3, padding: '0.55rem 1rem', cursor: 'pointer', fontFamily: 'Orbitron, monospace', fontSize: '0.52rem', letterSpacing: '0.12em', color: 'rgba(168,85,247,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', transition: 'all 0.15s ease' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(168,85,247,0.14)'; (e.currentTarget as HTMLElement).style.color = '#A855F7'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(168,85,247,0.06)'; (e.currentTarget as HTMLElement).style.color = 'rgba(168,85,247,0.7)'; }}>
                ◈ VIEW INTEGRATION GUIDE
              </button>
            </div>
            {/* OpenClaw */}
            <div style={{ background: 'linear-gradient(135deg, rgba(255,184,0,0.07) 0%, rgba(11,13,24,0.95) 100%)', border: '1px solid rgba(255,184,0,0.3)', borderRadius: 4, padding: '1.75rem', position: 'relative', overflow: 'hidden', clipPath: 'polygon(16px 0%,100% 0%,100% calc(100% - 16px),calc(100% - 16px) 100%,0% 100%,0% 16px)', transition: 'all 0.25s cubic-bezier(0.23,1,0.32,1)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,184,0,0.7)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 0 40px rgba(255,184,0,0.15)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,184,0,0.3)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent, #FFB800, transparent)' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ width: 44, height: 44, borderRadius: 4, background: 'rgba(255,184,0,0.12)', border: '1px solid rgba(255,184,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>🦀</div>
                <div>
                  <div style={{ fontFamily: 'Orbitron, monospace', fontWeight: 900, fontSize: '0.96rem', color: '#FFB800', letterSpacing: '0.1em', textShadow: '0 0 12px rgba(255,184,0,0.5)' }}>OPENCLAW</div>
                  <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.65rem', color: 'rgba(255,184,0,0.5)', letterSpacing: '0.15em' }}>OPEN-SOURCE AGENT RUNNER</div>
                </div>
              </div>
              <p style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: '0.9rem', color: 'rgba(232,244,255,0.55)', lineHeight: 1.65, marginBottom: '1.25rem' }}>
                An open-source desktop agent runtime that executes your Real AI Employees as autonomous task runners. Feed an agent a goal, and OpenClaw handles the tool calls, browser automation, and file operations — perfect for building self-running marketing, sales, or ops departments.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1.25rem' }}>
                {['Browser Automation', 'Tool Calling', 'File Operations', 'Open Source'].map(tag => (
                  <span key={tag} style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.48rem', letterSpacing: '0.08em', color: 'rgba(255,184,0,0.7)', background: 'rgba(255,184,0,0.07)', border: '1px solid rgba(255,184,0,0.2)', borderRadius: 2, padding: '0.25rem 0.6rem' }}>{tag}</span>
                ))}
              </div>
              <button onClick={() => { sfxClick(); window.open('https://openclaw.ai/', '_blank'); }}
                style={{ width: '100%', background: 'linear-gradient(135deg, rgba(255,184,0,0.2), rgba(255,184,0,0.08))', border: '1px solid rgba(255,184,0,0.45)', borderRadius: 3, padding: '0.7rem 1rem', cursor: 'pointer', fontFamily: 'Orbitron, monospace', fontWeight: 700, fontSize: '0.58rem', letterSpacing: '0.15em', color: '#FFB800', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', transition: 'all 0.2s ease', clipPath: 'polygon(6px 0%,100% 0%,100% calc(100% - 6px),calc(100% - 6px) 100%,0% 100%,0% 6px)' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,184,0,0.28)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 0 20px rgba(255,184,0,0.25)'; sfxHover(); }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'linear-gradient(135deg, rgba(255,184,0,0.2), rgba(255,184,0,0.08))'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}>
                <Download size={13} /> GET OPENCLAW
              </button>
              <button onClick={() => { sfxClick(); setActiveHarness('openclaw'); }}
                style={{ width: '100%', marginTop: '0.5rem', background: 'rgba(255,184,0,0.06)', border: '1px solid rgba(255,184,0,0.2)', borderRadius: 3, padding: '0.55rem 1rem', cursor: 'pointer', fontFamily: 'Orbitron, monospace', fontSize: '0.52rem', letterSpacing: '0.12em', color: 'rgba(255,184,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', transition: 'all 0.15s ease' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,184,0,0.14)'; (e.currentTarget as HTMLElement).style.color = '#FFB800'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,184,0,0.06)'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,184,0,0.7)'; }}>
                ◈ VIEW INTEGRATION GUIDE
              </button>
            </div>
            {/* Claude Coworker */}
            <div style={{ background: 'linear-gradient(135deg, rgba(255,140,60,0.07) 0%, rgba(11,13,24,0.95) 100%)', border: '1px solid rgba(255,140,60,0.3)', borderRadius: 4, padding: '1.75rem', position: 'relative', overflow: 'hidden', clipPath: 'polygon(16px 0%,100% 0%,100% calc(100% - 16px),calc(100% - 16px) 100%,0% 100%,0% 16px)', transition: 'all 0.25s cubic-bezier(0.23,1,0.32,1)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,140,60,0.7)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 0 40px rgba(255,140,60,0.15)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,140,60,0.3)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent, #FF8C3C, transparent)' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ width: 44, height: 44, borderRadius: 4, background: 'rgba(255,140,60,0.12)', border: '1px solid rgba(255,140,60,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>🤝</div>
                <div>
                  <div style={{ fontFamily: 'Orbitron, monospace', fontWeight: 900, fontSize: '0.96rem', color: '#FF8C3C', letterSpacing: '0.1em', textShadow: '0 0 12px rgba(255,140,60,0.5)' }}>CLAUDE COWORKER</div>
                  <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.65rem', color: 'rgba(255,140,60,0.5)', letterSpacing: '0.15em' }}>ANTHROPIC · CLAUDE.AI</div>
                </div>
              </div>
              <p style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: '0.9rem', color: 'rgba(232,244,255,0.55)', lineHeight: 1.65, marginBottom: '1.25rem' }}>
                Deploy your Real AI Employees directly inside Claude's Projects feature as persistent coworkers. Each agent retains its full persona, expertise, and role context across every session — enabling you to build a complete AI department that collaborates with you inside Claude's interface.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1.25rem' }}>
                {['Persistent Context', 'Projects Integration', 'Team Collaboration', 'Instant Setup'].map(tag => (
                  <span key={tag} style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.48rem', letterSpacing: '0.08em', color: 'rgba(255,140,60,0.7)', background: 'rgba(255,140,60,0.07)', border: '1px solid rgba(255,140,60,0.2)', borderRadius: 2, padding: '0.25rem 0.6rem' }}>{tag}</span>
                ))}
              </div>
              <button onClick={() => { sfxClick(); window.open('https://coworkerai.io/', '_blank'); }}
                style={{ width: '100%', background: 'linear-gradient(135deg, rgba(255,140,60,0.2), rgba(255,140,60,0.07))', border: '1px solid rgba(255,140,60,0.45)', borderRadius: 3, padding: '0.7rem 1rem', cursor: 'pointer', fontFamily: 'Orbitron, monospace', fontWeight: 700, fontSize: '0.58rem', letterSpacing: '0.15em', color: '#FF8C3C', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', transition: 'all 0.2s ease', clipPath: 'polygon(6px 0%,100% 0%,100% calc(100% - 6px),calc(100% - 6px) 100%,0% 100%,0% 6px)' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,140,60,0.28)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 0 20px rgba(255,140,60,0.25)'; sfxHover(); }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'linear-gradient(135deg, rgba(255,140,60,0.2), rgba(255,140,60,0.07))'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}>
                <ExternalLink size={13} /> OPEN CLAUDE.AI
              </button>
              <button onClick={() => { sfxClick(); setActiveHarness('claude'); }}
                style={{ width: '100%', marginTop: '0.5rem', background: 'rgba(255,140,60,0.06)', border: '1px solid rgba(255,140,60,0.2)', borderRadius: 3, padding: '0.55rem 1rem', cursor: 'pointer', fontFamily: 'Orbitron, monospace', fontSize: '0.52rem', letterSpacing: '0.12em', color: 'rgba(255,140,60,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', transition: 'all 0.15s ease' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,140,60,0.14)'; (e.currentTarget as HTMLElement).style.color = '#FF8C3C'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,140,60,0.06)'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,140,60,0.7)'; }}>
                ◈ VIEW INTEGRATION GUIDE
              </button>
            </div>
          </div>
          <div style={{ marginTop: '2.5rem', textAlign: 'center', padding: '1.5rem', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,166,235,0.12)', borderRadius: 4 }}>
            <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.68rem', letterSpacing: '0.2em', color: 'rgba(0,166,235,0.5)', marginBottom: '0.5rem' }}>◈ HOW IT WORKS ◈</div>
            <p style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: '0.95rem', color: 'rgba(232,244,255,0.45)', lineHeight: 1.7, maxWidth: 700, margin: '0 auto' }}>
              Purchase any Real AI Employee → receive their encrypted agent package → load them into your chosen harness → assign roles, chain workflows, and build entire autonomous departments. One purchase, infinite deployment options.
            </p>
          </div>
        </div>
      </section>



      {/* ── SOCIAL PROOF TICKER ───────────────────────────── */}
      <div style={{ background: 'rgba(0,0,0,0.6)', borderTop: '1px solid rgba(0,166,235,0.2)', borderBottom: '1px solid rgba(0,166,235,0.2)', padding: '0.75rem 0', overflow: 'hidden', position: 'relative', zIndex: 2 }}>
        <div style={{ display: 'inline-flex', gap: '3rem', animation: 'marqueeScroll 30s linear infinite', whiteSpace: 'nowrap' }}>
          {[...Array(3)].map((_, rep) => (
            <span key={rep} style={{ display: 'inline-flex', gap: '3rem' }}>
              {[
                '⚡ Marcus T. just deployed the Sales Closer Agent',
                '🚀 Sarah K. built her Marketing Department in 8 minutes',
                '💰 Revenue up 340% after deploying Finance AI Team',
                '⭐ "Best AI Workforce platform of 2025" — TechFounder Weekly',
                '🤖 2,847 businesses running on RealAI Employees',
                '✅ 60-day money-back guarantee — zero risk',
                '🔥 Enterprise & Specialized Dept: 20 agents, one deployment',
                '📈 Average user deploys 4.2 agents in first 24 hours',
              ].map((msg, j) => (
                <span key={j} style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.74rem', color: 'rgba(0,166,235,0.7)', letterSpacing: '0.1em' }}>
                  {msg}
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      {/* ── FINAL CTA ──────────────────────────────────────── */}
      <section ref={el => { sectionsRef.current['bundles'] = el; }} style={{ padding: '6rem 0 8rem', position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(0,166,235,0.3), rgba(255,0,85,0.2), rgba(0,166,235,0.3), transparent)', marginBottom: '5rem' }} />

        {/* Background glow */}
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(0,166,235,0.04) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="section-label" style={{ justifyContent: 'center', marginBottom: '1.5rem' }}>◈ DEPLOYMENT PORTAL ◈</div>
          <h2 style={{
            fontFamily: 'Orbitron, monospace', fontWeight: 900,
            fontSize: 'clamp(2rem, 5vw, 4rem)',
            lineHeight: 1.1, marginBottom: '1.5rem',
          }}>
            <span style={{ color: 'rgba(232,244,255,0.9)' }}>BUILD YOUR </span>
            <span style={{ background: 'linear-gradient(135deg, #00A6EB, #FF0055)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>AI TEAM</span>
            <span style={{ color: 'rgba(232,244,255,0.9)' }}> NOW</span>
          </h2>
          <p style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: '1.1rem', color: 'rgba(232,244,255,0.45)', maxWidth: 500, margin: '0 auto 2.5rem', lineHeight: 1.6 }}>
            Join thousands of forward-thinking businesses already running on AI employees. No contracts. No overhead. Maximum results.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn-cyber" style={{ fontSize: '0.84rem', padding: '0.9rem 2.5rem' }} onClick={() => scrollToSection('departments')}>
              <Zap size={14} /> BROWSE DEPARTMENTS
            </button>
            <button className="btn-cyber btn-magenta" style={{ fontSize: '0.84rem', padding: '0.9rem 2.5rem' }} onClick={() => { sfxClick(); navigate('/matchmaker'); }}>
              <Target size={14} /> FIND MY MATCH
            </button>
            <Link href="/bundle-builder">
              <button className="btn-cyber" style={{ fontSize: '0.84rem', padding: '0.9rem 2.5rem', borderColor: 'rgba(0,255,136,0.5)', color: '#00FF88' }} onClick={() => sfxClick()}>
                <Package size={14} /> BUNDLE BUILDER
              </button>
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div style={{ marginTop: '5rem', borderTop: '1px solid rgba(0,166,235,0.1)', paddingTop: '2rem' }}>
          {/* Footer links row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '1.5rem' }}>
            {/* Left: parent company */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.68rem', color: 'rgba(232,244,255,0.3)', letterSpacing: '0.15em' }}>A PRODUCT OF</span>
              <a
                href="https://elitenexusai.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontFamily: 'Orbitron, monospace', fontWeight: 700, fontSize: '0.78rem',
                  letterSpacing: '0.2em', color: '#00A6EB',
                  textDecoration: 'none',
                  textShadow: '0 0 10px rgba(0,166,235,0.6)',
                  transition: 'all 0.18s ease',
                  borderBottom: '1px solid rgba(0,166,235,0.3)',
                  paddingBottom: '1px',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.textShadow = '0 0 20px rgba(0,166,235,1), 0 0 40px rgba(0,166,235,0.5)'; (e.currentTarget as HTMLAnchorElement).style.borderBottomColor = '#00A6EB'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.textShadow = '0 0 10px rgba(0,166,235,0.6)'; (e.currentTarget as HTMLAnchorElement).style.borderBottomColor = 'rgba(0,166,235,0.3)'; }}
              >
                ELITE NEXUS AI
              </a>
            </div>
            {/* Center: brand */}
            <div style={{ fontFamily: 'Orbitron, monospace', fontSize: '0.65rem', color: 'rgba(0,166,235,0.25)', letterSpacing: '0.3em', textAlign: 'center' }}>
              REALAIEMPLOYEES.COM — THE FUTURE OF WORKFORCE IS HERE
            </div>
            {/* Right: legal links */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
              <a
                href="/terms"
                style={{
                  fontFamily: 'Share Tech Mono, monospace', fontSize: '0.68rem',
                  color: 'rgba(232,244,255,0.3)', letterSpacing: '0.15em',
                  textDecoration: 'none', transition: 'color 0.18s ease',
                }}
                onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.color = '#00A6EB'}
                onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(232,244,255,0.3)'}
              >
              TERMS OF SERVICE
              </a>
              <span style={{ color: 'rgba(0,166,235,0.2)', fontSize: '0.65rem' }}>◈</span>
              <a
                href="/privacy"
                style={{
                  fontFamily: 'Share Tech Mono, monospace', fontSize: '0.68rem',
                  color: 'rgba(232,244,255,0.3)', letterSpacing: '0.15em',
                  textDecoration: 'none', transition: 'color 0.18s ease',
                }}
                onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.color = '#00A6EB'}
                onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(232,244,255,0.3)'}
              >
                PRIVACY POLICY
              </a>
              <span style={{ color: 'rgba(0,166,235,0.2)', fontSize: '0.65rem' }}>◈</span>
              <span style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.65rem', color: 'rgba(232,244,255,0.2)', letterSpacing: '0.1em' }}>
                © {new Date().getFullYear()} ELITE NEXUS AI
              </span>
            </div>
          </div>
          {/* Bottom gradient line */}
          <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(0,166,235,0.3), rgba(255,0,85,0.2), rgba(0,166,235,0.3), transparent)' }} />
        </div>
      </section>

      {/* ── TALENT MATCHMAKER WIZARD ───────────────────────── */}
      {matchWizardOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(16px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
        }}
          onClick={() => { setMatchWizardOpen(false); setWizardStep(0); setWizardAnswers({}); setWizardResults([]); }}
        >
          <div
            style={{
              width: '100%', maxWidth: 600,
              background: 'linear-gradient(135deg, rgba(11,13,24,0.99), rgba(7,9,18,0.99))',
              border: '1px solid rgba(0,166,235,0.4)',
              boxShadow: '0 0 80px rgba(0,166,235,0.15)',
              clipPath: 'polygon(16px 0%, 100% 0%, 100% calc(100% - 16px), calc(100% - 16px) 100%, 0% 100%, 0% 16px)',
              padding: '2.5rem',
              animation: 'fadeInScale 0.3s ease',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem' }}>
              <div>
                <div className="section-label" style={{ marginBottom: '0.25rem' }}>◈ AI TALENT MATCHMAKER ◈</div>
                <div style={{ fontFamily: 'Orbitron, monospace', fontWeight: 900, fontSize: '1.1rem', color: '#00A6EB' }}>
                  FIND YOUR PERFECT AGENTS
                </div>
              </div>
              <button onClick={() => { setMatchWizardOpen(false); setWizardStep(0); setWizardAnswers({}); setWizardResults([]); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(232,244,255,0.4)', fontSize: '1.2rem' }}>✕</button>
            </div>

            {/* Progress */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem' }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{ flex: 1, height: 3, background: wizardStep > i ? '#00A6EB' : 'rgba(0,166,235,0.15)', transition: 'background 0.3s ease', boxShadow: wizardStep > i ? '0 0 8px rgba(0,166,235,0.5)' : 'none' }} />
              ))}
            </div>

            {/* Steps */}
            {wizardStep < 3 ? (
              <div>
                <div style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: '1.1rem', fontWeight: 600, color: 'rgba(232,244,255,0.8)', marginBottom: '1.5rem', lineHeight: 1.4 }}>
                  {wizardQuestions[wizardStep]?.label}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  {wizardQuestions[wizardStep]?.options.map((opt, i) => (
                    <button key={i}
                      onClick={() => {
                        const newAnswers = { ...wizardAnswers, [wizardQuestions[wizardStep].id]: opt };
                        setWizardAnswers(newAnswers);
                        if (wizardStep < 2) setWizardStep(s => s + 1);
                        else { setWizardAnswers(newAnswers); setTimeout(() => runWizard(), 50); }
                      }}
                      style={{
                        background: 'rgba(0,166,235,0.06)', border: '1px solid rgba(0,166,235,0.2)',
                        borderRadius: 3, padding: '0.75rem 1rem', cursor: 'pointer',
                        fontFamily: 'Share Tech Mono, monospace', fontSize: '0.78rem',
                        color: 'rgba(232,244,255,0.7)', letterSpacing: '0.05em',
                        textAlign: 'left', transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(0,166,235,0.15)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,166,235,0.5)'; (e.currentTarget as HTMLElement).style.color = '#00A6EB'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(0,166,235,0.06)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,166,235,0.2)'; (e.currentTarget as HTMLElement).style.color = 'rgba(232,244,255,0.7)'; }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* Results */
              <div>
                <div style={{ fontFamily: 'Orbitron, monospace', fontSize: '0.84rem', color: '#00FF88', letterSpacing: '0.2em', marginBottom: '1.5rem', textAlign: 'center' }}>
                  ◈ TOP MATCHES IDENTIFIED ◈
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
                  {wizardResults.map((emp, i) => {
                    const config = DEPT_CONFIG[emp.departmentId] || { color: '#00A6EB', gradient: '', tagline: '' };
                    return (
                      <div key={emp.id} style={{
                        display: 'flex', alignItems: 'center', gap: '1rem',
                        background: 'rgba(0,166,235,0.05)', border: '1px solid rgba(0,166,235,0.2)',
                        borderRadius: 3, padding: '0.75rem 1rem',
                        cursor: 'pointer', transition: 'all 0.2s ease',
                      }}
                        onClick={() => { setSelectedEmployee(emp); setDossierOpen(true); setMatchWizardOpen(false); }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = config.color; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,166,235,0.2)'; }}
                      >
                        <div style={{
                          width: 40, height: 40, borderRadius: 3, flexShrink: 0,
                          background: `rgba(${hexToRgb(config.color)}, 0.15)`,
                          border: `1px solid rgba(${hexToRgb(config.color)}, 0.3)`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontFamily: 'Orbitron, monospace', fontWeight: 900, fontSize: '0.84rem', color: config.color,
                        }}>
                          #{i + 1}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: 'Orbitron, monospace', fontSize: '0.78rem', color: 'rgba(232,244,255,0.9)', fontWeight: 700, marginBottom: '0.2rem' }}>{emp.name}</div>
                          <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.68rem', color: config.color }}>{emp.roleTitle}</div>
                        </div>
                        <div style={{ fontFamily: 'Orbitron, monospace', fontSize: '0.84rem', color: '#FFB800', fontWeight: 900 }}>${(emp.price ?? 297).toLocaleString()}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button className="btn-cyber" style={{ flex: 1, justifyContent: 'center', fontSize: '0.74rem' }}
                    onClick={() => { wizardResults.forEach(e => handleAddToCart(e)); setMatchWizardOpen(false); setCartOpen(true); }}>
                    <ShoppingBag size={12} /> DEPLOY ALL 3
                  </button>
                  <button onClick={() => { setWizardStep(0); setWizardAnswers({}); setWizardResults([]); }}
                    style={{ background: 'rgba(255,0,85,0.08)', border: '1px solid rgba(255,0,85,0.2)', borderRadius: 3, padding: '0.5rem 1rem', cursor: 'pointer', color: 'rgba(255,0,85,0.6)', fontFamily: 'Orbitron, monospace', fontSize: '0.68rem', letterSpacing: '0.1em' }}>
                    RETRY
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Harness Integration Modals */}
      {activeHarness && (() => {
        const cfg = HARNESS_CONFIGS.find(h => h.id === activeHarness);
        return cfg ? <HarnessModal config={cfg} onClose={() => setActiveHarness(null)} /> : null;
      })()}
    </div>
  );
}

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0,166,235';
  return `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`;
}
